import { useState } from "react";
import { ArrowLeft, MapPin, Radio, Search, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useCasts } from "@/hooks/useCasts";
import { useToast } from "@/components/ui/use-toast";
import { kmToMiles } from "@/utils/distanceUtils";

interface CreateCastPostProps {
  onBack: () => void;
}

const radiusOptions = [
  { value: "5", label: "3 miles (5 km)", miles: 3 },
  { value: "10", label: "6 miles (10 km)", miles: 6 },
  { value: "25", label: "16 miles (25 km)", miles: 16 },
  { value: "50", label: "31 miles (50 km)", miles: 31 },
  { value: "100", label: "62 miles (100 km)", miles: 62 },
  { value: "250", label: "155 miles (250 km)", miles: 155 },
  { value: "480", label: "298 miles (480 km)", miles: 298 },
  { value: "800", label: "497 miles (800 km)", miles: 497 },
  { value: "1600", label: "994+ miles (1600+ km)", miles: 994 }
];

const sentimentOptions = [
  { id: "standard", label: "General Request", color: "text-blue-600", bgColor: "bg-blue-50", borderColor: "border-blue-200" },
  { id: "help", label: "Urgent Need", color: "text-orange-600", bgColor: "bg-orange-50", borderColor: "border-orange-200" }
];

const adPlacementDurationOptions = [
  { value: "1", label: "1 Day", description: "Keep in feed for 24 hours" },
  { value: "7", label: "7 Days", description: "Keep in feed for 1 week" },
  { value: "14", label: "2 Weeks", description: "Keep in feed for 14 days" }
];

export const CreateCastPost = ({ onBack }: CreateCastPostProps) => {
  const { addCast } = useCasts();
  const { toast } = useToast();
  
  const [isoRequest, setIsoRequest] = useState("");
  const [selectedRadius, setSelectedRadius] = useState("");
  const [location, setLocation] = useState("");
  const [broadcastLat, setBroadcastLat] = useState<number | null>(null);
  const [broadcastLng, setBroadcastLng] = useState<number | null>(null);
  const [sentiment, setSentiment] = useState("standard");
  const [adPlacementDuration, setAdPlacementDuration] = useState("7");
  const [isPosting, setIsPosting] = useState(false);
  const [isGettingLocation, setIsGettingLocation] = useState(false);

  const reverseGeocode = async (lat: number, lng: number): Promise<string> => {
    try {
      // Use OpenStreetMap Nominatim (free geocoding service)
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json&addressdetails=1`
      );
      
      if (!response.ok) {
        throw new Error('Geocoding service unavailable');
      }
      
      const data = await response.json();
      if (data && data.address) {
        const address = data.address;
        
        // For US locations, prioritize city, state format
        if (address.country_code === 'us') {
          const city = address.city || address.town || address.village || address.county;
          const state = address.state;
          
          if (city && state) {
            // Extract state abbreviation if it's a full name
            const stateAbbreviations: { [key: string]: string } = {
              'Georgia': 'GA', 'California': 'CA', 'Texas': 'TX', 'New York': 'NY',
              'Florida': 'FL', 'Illinois': 'IL', 'Pennsylvania': 'PA', 'Ohio': 'OH',
              // Add more as needed
            };
            const stateCode = stateAbbreviations[state] || state;
            return `${city}, ${stateCode}`;
          }
        }
        
        // For other countries or fallback
        const city = address.city || address.town || address.village;
        const country = address.country;
        
        if (city && country) {
          return `${city}, ${country}`;
        }
        
        // Use the display name as fallback
        if (data.display_name) {
          const parts = data.display_name.split(',');
          if (parts.length >= 2) {
            return `${parts[0].trim()}, ${parts[1].trim()}`;
          }
        }
      }
      
      // Final fallback to coordinates
      return `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
    } catch (error) {
      console.error('Reverse geocoding failed:', error);
      // Fallback to coordinates
      return `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
    }
  };

  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      toast({
        title: "Location not supported",
        description: "Your browser doesn't support location services",
        variant: "destructive"
      });
      return;
    }

    setIsGettingLocation(true);

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        
        setBroadcastLat(lat);
        setBroadcastLng(lng);
        
        // Convert coordinates to city, state
        const cityState = await reverseGeocode(lat, lng);
        setLocation(cityState);
        
        setIsGettingLocation(false);
        toast({
          title: "Location set",
          description: `Your location has been set to ${cityState}`,
        });
      },
      (error) => {
        console.error("Location error:", error);
        setIsGettingLocation(false);
        toast({
          title: "Location error",
          description: "Could not get your current location. Please enter manually.",
          variant: "destructive"
        });
      }
    );
  };

  const handlePost = async () => {
    if (!isoRequest.trim() || !selectedRadius || !location || !broadcastLat || !broadcastLng) {
      toast({
        title: "Missing information",
        description: "Please fill in your ISO request, select a radius, and set your location",
        variant: "destructive"
      });
      return;
    }

    setIsPosting(true);

    try {
      const castData = {
        iso_request: isoRequest,
        location_radius_km: parseInt(selectedRadius),
        broadcast_location_lat: broadcastLat,
        broadcast_location_lng: broadcastLng,
        location_address: location,
        sentiment: sentiment,
        ad_placement_duration_days: parseInt(adPlacementDuration)
      };

      console.log("📡 Creating Cast with data:", castData);

      await addCast(castData);

      const selectedOption = radiusOptions.find(opt => opt.value === selectedRadius);
      const durationLabel = adPlacementDurationOptions.find(opt => opt.value === adPlacementDuration)?.label || "7 Days";

      toast({
        title: "Cast posted!",
        description: `Your ISO request has been broadcast within ${selectedOption?.miles || Math.round(kmToMiles(parseInt(selectedRadius)))} miles and will appear in the home feed for ${durationLabel}`,
      });

      onBack();
    } catch (error) {
      console.error('Error creating Cast:', error);
      toast({
        title: "Failed to create Cast",
        description: "Something went wrong. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsPosting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 p-4 flex items-center justify-between">
        <button onClick={onBack} className="p-2 hover:bg-gray-100 rounded-full">
          <ArrowLeft size={24} className="text-gray-600" />
        </button>
        <h2 className="text-lg font-semibold">Create Cast</h2>
        <Button
          onClick={handlePost}
          disabled={!isoRequest.trim() || !selectedRadius || !location || !broadcastLat || !broadcastLng || isPosting}
          className="h-10 px-4 rounded-xl bg-iso-solid text-white hover:bg-violet-700 active:bg-violet-800 focus:ring-2 focus:ring-violet-300"
        >
          {isPosting ? "Broadcasting..." : "Cast"}
        </Button>
      </div>

      <div className="p-4 space-y-6">
        {/* Cast Overview */}
        <div className="bg-gradient-to-r from-iso-soft-bg to-pink-50 rounded-xl p-4 border border-iso-soft-border">
          <div className="flex items-center gap-2 mb-2">
            <Radio className="text-iso-solid" size={20} />
            <h3 className="font-medium text-[#3E1C8A]">Broadcast ISO Request</h3>
          </div>
          <p className="text-sm text-[#3E1C8A]">
            Cast your "In Search Of" request to farmers within your selected radius. 
            Perfect for finding specific crops, equipment, or expertise.
          </p>
        </div>

        {/* ISO Request */}
        <div className="bg-white rounded-xl p-4 space-y-3">
          <h3 className="font-medium text-ink-primary flex items-center gap-2">
            <Search size={18} className="text-iso-solid" />
            What are you looking for? *
          </h3>
          <Textarea
            placeholder="I'm looking for organic tomatoes for immediate pickup, or expertise with soil pH management, or used tractor equipment..."
            value={isoRequest}
            onChange={(e) => setIsoRequest(e.target.value)}
            disabled={isPosting}
            className="min-h-[100px]"
          />
          <p className="text-xs text-gray-500">
            Be specific about what you need, quantities, timing, and any other requirements
          </p>
        </div>

        {/* Broadcast Range */}
        <div className="bg-white rounded-xl p-4 space-y-3">
          <h3 className="font-medium text-gray-800">Broadcast Range *</h3>
          <Select value={selectedRadius} onValueChange={setSelectedRadius}>
            <SelectTrigger>
              <SelectValue placeholder="Select how far to broadcast..." />
            </SelectTrigger>
            <SelectContent>
              {radiusOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {selectedRadius && (
            <div className="text-sm text-[#3E1C8A] bg-iso-soft-bg p-2 rounded">
              📡 Your request will reach farmers within {radiusOptions.find(r => r.value === selectedRadius)?.label}
            </div>
          )}
        </div>

        {/* Location */}
        <div className="bg-white rounded-xl p-4 space-y-3">
          <h3 className="font-medium text-ink-primary flex items-center gap-2">
            <MapPin size={18} className="text-iso-solid" />
            Broadcast Location *
          </h3>
          <div className="space-y-3">
            <Button
              type="button"
              onClick={getCurrentLocation}
              disabled={isPosting || isGettingLocation}
              variant="outline"
              className="w-full"
            >
              <MapPin size={16} className="mr-2" />
              {isGettingLocation ? "Getting Location..." : "Use Current Location"}
            </Button>
            <div className="text-center text-sm text-gray-500">OR</div>
            <Input
              placeholder="Enter your location (e.g., Sacramento, CA)"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              disabled={isPosting}
            />
          </div>
          {location && broadcastLat && broadcastLng && (
            <div className="text-sm text-green-700 bg-green-50 p-2 rounded">
              ✓ Location set: {location}
            </div>
          )}
        </div>

        {/* Ad Placement Duration */}
        <div className="bg-white rounded-xl p-4 space-y-3">
          <h3 className="font-medium text-ink-primary flex items-center gap-2">
            <Clock size={18} className="text-iso-solid" />
            Home Feed Duration
          </h3>
          <p className="text-sm text-ink-body">
            Choose how long your Cast will appear in the Ad Placement on the Home feed
          </p>
          <Select value={adPlacementDuration} onValueChange={setAdPlacementDuration}>
            <SelectTrigger>
              <SelectValue placeholder="Select duration..." />
            </SelectTrigger>
            <SelectContent>
              {adPlacementDurationOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  <div>
                    <div className="font-medium">{option.label}</div>
                    <div className="text-sm text-gray-500">{option.description}</div>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {adPlacementDuration && (
            <div className="text-sm text-[#3E1C8A] bg-iso-soft-bg p-2 rounded">
              ⏰ Your Cast will appear in the home feed for {adPlacementDurationOptions.find(opt => opt.value === adPlacementDuration)?.label}
            </div>
          )}
        </div>

        {/* Priority Level */}
        <div className="bg-white rounded-xl p-4 space-y-3">
          <h3 className="font-medium text-gray-800">Priority Level</h3>
          <RadioGroup value={sentiment} onValueChange={setSentiment} className="grid grid-cols-1 gap-3">
            {sentimentOptions.map((option) => (
              <div key={option.id} className="flex items-center">
                <RadioGroupItem value={option.id} id={option.id} className="sr-only" />
                <label 
                  htmlFor={option.id}
                  className={`flex items-center justify-center space-x-2 p-3 rounded-lg border cursor-pointer transition-all w-full ${
                    sentiment === option.id 
                      ? `${option.bgColor} ${option.borderColor} ${option.color}` 
                      : `bg-gray-50 border-gray-200 text-gray-600 hover:${option.bgColor}`
                  }`}
                >
                  <span className="font-medium">{option.label}</span>
                </label>
              </div>
            ))}
          </RadioGroup>
        </div>

        {/* Info Box */}
        <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
          <h4 className="font-medium text-blue-800 mb-2">How Cast Works</h4>
          <ul className="text-sm text-blue-700 space-y-1">
            <li>• Your ISO request is broadcast to farmers in your selected radius</li>
            <li>• Farmers can respond directly through messages</li>
            <li>• Your location is only shared as a general area for privacy</li>
            <li>• Cast posts will appear in the home feed for your selected duration</li>
          </ul>
        </div>
      </div>
    </div>
  );
};
